import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Shield, FileText, Scale, Gavel, Clock, Lightbulb, Target, Zap } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import SectionTitle from "@/components/SectionTitle";
import ContactForm from "@/components/ContactForm";

const Enforcement = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="pt-24 sm:pt-32 pb-12 sm:pb-16 px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mb-4 sm:mb-6">
            ייצוג וליווי משפטי בהליכי הוצאה לפועל
          </h1>
          <h2 className="text-lg sm:text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto mb-6 sm:mb-8 leading-relaxed">
            בין אם אתם זקוקים להגנה מפני נושים או לגביית חוב המגיע לכם – משרדנו יפעל בנחישות וביעילות למיצוי זכויותיכם.
          </h2>
          <Link to="/contact">
            <Button size="lg" className="text-base sm:text-lg px-6 sm:px-8 w-full sm:w-auto">
              לייעוץ משפטי דחוף
            </Button>
          </Link>
        </div>
      </section>

      {/* Opening Section */}
      <section className="py-12 sm:py-16 px-4 bg-muted/30">
        <div className="container mx-auto max-w-4xl">
          <p className="text-base sm:text-lg text-foreground leading-relaxed mb-4 sm:mb-6">
            מערכת ההוצאה לפועל היא זירה מורכבת ואינטנסיבית, הדורשת פעולה מהירה וצעדים משפטיים מדויקים. קבלת מכתב התראה, גילוי עיקול בחשבון הבנק או התמודדות עם צ'ק שחזר – כל אלו יוצרים לחץ ודורשים טיפול מקצועי מיידי.
          </p>
          <p className="text-base sm:text-lg text-foreground leading-relaxed mb-4 sm:mb-6">
            התנהלות לא נכונה מול לשכות ההוצאה לפועל עלולה להוביל להפסד כספי ניכר, הטלת הגבלות חמורות ופגיעה משמעותית בשגרת החיים.
          </p>
          <p className="text-base sm:text-lg text-foreground leading-relaxed font-medium">
            משרדנו מתמחה בניהול הליכים משני צדי המתרס – ייצוג חייבים להגנה על זכויותיהם וייצוג זוכים לגבייה יעילה של חובותיהם. אנו כאן כדי לפעול עבורכם.
          </p>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-12 sm:py-16 md:py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <SectionTitle 
            title="השירותים שלנו בתחום ההוצאה לפועל"
            subtitle="אנו מציעים פתרונות משפטיים מקיפים המותאמים למצבכם הייחודי"
            align="center"
            className="mb-8 sm:mb-12"
          />
          
          <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
            {/* Debtors Service */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
                  <Shield className="w-10 h-10 sm:w-12 sm:h-12 text-primary flex-shrink-0" />
                  <CardTitle className="text-xl sm:text-2xl">ייצוג חייבים (הגנה ופתרונות)</CardTitle>
                </div>
                <CardDescription className="text-sm sm:text-base">
                  כאשר נפתח נגדך תיק הוצאה לפועל, הזמן הוא גורם קריטי. אנו נפעל באופן מיידי כדי להגן עליך:
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 sm:space-y-4">
                  <li className="flex gap-3">
                    <span className="text-primary font-bold flex-shrink-0">•</span>
                    <div>
                      <strong className="text-sm sm:text-base">ביטול עיקולים והגבלות:</strong>
                      <p className="text-muted-foreground text-sm sm:text-base mt-1">פעולה מהירה לביטול עיקולי בנק, משכורת, רכב וביטול הגבלות (כגון רישיון נהיגה, עיכוב יציאה מהארץ).</p>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-primary font-bold flex-shrink-0">•</span>
                    <div>
                      <strong className="text-sm sm:text-base">התנגדות לביצוע שטר/תביעה:</strong>
                      <p className="text-muted-foreground text-sm sm:text-base mt-1">הגשת התנגדות מקצועית ומנומקת לביצוע צ'ק, שטר חוב או תביעה על סכום קצוב.</p>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-primary font-bold flex-shrink-0">•</span>
                    <div>
                      <strong className="text-sm sm:text-base">חקירת יכולת וצו תשלומים:</strong>
                      <p className="text-muted-foreground text-sm sm:text-base mt-1">ייצוג בחקירת יכולת והשגת צו תשלומים חודשי ריאלי, המותאם ליכולתך הכלכלית.</p>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-primary font-bold flex-shrink-0">•</span>
                    <div>
                      <strong className="text-sm sm:text-base">איחוד תיקים:</strong>
                      <p className="text-muted-foreground text-sm sm:text-base mt-1">הגשת בקשה לאיחוד כל התיקים נגדך תחת קורת גג אחת ותשלום חודשי מוסכם.</p>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-primary font-bold flex-shrink-0">•</span>
                    <div>
                      <strong className="text-sm sm:text-base">טענת "פרעתי":</strong>
                      <p className="text-muted-foreground text-sm sm:text-base mt-1">הגשת טענה במקרה ששילמת את החוב (כולו או חלקו) והתיק נותר פתוח.</p>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-primary font-bold flex-shrink-0">•</span>
                    <div>
                      <strong className="text-sm sm:text-base">ליווי בדרך לחדלות פירעון:</strong>
                      <p className="text-muted-foreground text-sm sm:text-base mt-1">במקרים מתאימים, אנו נבחן ונייעץ על פנייה להליך חדלות פירעון (פשיטת רגל) כפתרון לסגירת כל החובות.</p>
                    </div>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Creditors Service */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
                  <Gavel className="w-10 h-10 sm:w-12 sm:h-12 text-primary flex-shrink-0" />
                  <CardTitle className="text-xl sm:text-2xl">ייצוג זוכים (גביית חובות)</CardTitle>
                </div>
                <CardDescription className="text-sm sm:text-base">
                  אנו פועלים בנחישות ובכל האמצעים החוקיים כדי לגבות את הכסף המגיע לך:
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 sm:space-y-4">
                  <li className="flex gap-3">
                    <span className="text-primary font-bold flex-shrink-0">•</span>
                    <div>
                      <strong className="text-sm sm:text-base">פתיחת תיקי הוצאה לפועל:</strong>
                      <p className="text-muted-foreground text-sm sm:text-base mt-1">פתיחת תיקים לביצוע פסקי דין, צ'קים חוזרים, שטרי חוב, תביעות ועוד.</p>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-primary font-bold flex-shrink-0">•</span>
                    <div>
                      <strong className="text-sm sm:text-base">איתור נכסים וכתובות:</strong>
                      <p className="text-muted-foreground text-sm sm:text-base mt-1">שימוש באמצעים מתקדמים לאיתור נכסים, מקורות הכנסה וכתובות של חייבים.</p>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-primary font-bold flex-shrink-0">•</span>
                    <div>
                      <strong className="text-sm sm:text-base">הטלת עיקולים והגבלות:</strong>
                      <p className="text-muted-foreground text-sm sm:text-base mt-1">נקיטת סנקציות מהירות ויעילות, כולל עיקול חשבונות בנק, משכורות, נכסי מקרקעין, רכבים ומיטלטלין.</p>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-primary font-bold flex-shrink-0">•</span>
                    <div>
                      <strong className="text-sm sm:text-base">הטלת הגבלות חמורות:</strong>
                      <p className="text-muted-foreground text-sm sm:text-base mt-1">הגבלת רישיון נהיגה, מניעת יציאה מהארץ, הגבלת שימוש בכרטיסי אשראי ועוד.</p>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-primary font-bold flex-shrink-0">•</span>
                    <div>
                      <strong className="text-sm sm:text-base">ניהול הליכי כינוס נכסים:</strong>
                      <p className="text-muted-foreground text-sm sm:text-base mt-1">מינוי כונס נכסים למכירת נכסי החייב לצורך פירעון החוב.</p>
                    </div>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-12 sm:py-16 md:py-20 px-4 bg-muted/30">
        <div className="container mx-auto max-w-6xl">
          <SectionTitle 
            title="מדוע לבחור במשרדנו לניהול תיק ההוצאה לפועל?"
            align="center"
            className="mb-8 sm:mb-12"
          />
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex justify-center mb-3 sm:mb-4">
                  <Clock className="w-12 h-12 sm:w-16 sm:h-16 text-primary" />
                </div>
                <CardTitle className="text-lg sm:text-xl mb-2 sm:mb-3">זמינות ומהירות תגובה</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm sm:text-base">
                  בהוצאה לפועל, כל דקה קובעת. אנו זמינים לטיפול מיידי ודחוף, מבינים את חשיבות הזמן ופועלים בהתאם.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex justify-center mb-3 sm:mb-4">
                  <Scale className="w-12 h-12 sm:w-16 sm:h-16 text-primary" />
                </div>
                <CardTitle className="text-lg sm:text-xl mb-2 sm:mb-3">בקיאות וניסיון מוכח</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm sm:text-base">
                  אנו מכירים את המערכת, את הרשמים ואת דרכי הפעולה מכל צדדיה. הניסיון שלנו מאפשר לנו לצפות מהלכים ולפעול אסטרטגית.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex justify-center mb-3 sm:mb-4">
                  <Zap className="w-12 h-12 sm:w-16 sm:h-16 text-primary" />
                </div>
                <CardTitle className="text-lg sm:text-xl mb-2 sm:mb-3">נחישות ללא פשרות</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm sm:text-base">
                  בין אם אנו מגנים עליך או גובים עבורך, אנו פועלים באסרטיביות ובנחישות כדי להשיג את התוצאה הטובה ביותר האפשרית עבורך.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex justify-center mb-3 sm:mb-4">
                  <Lightbulb className="w-12 h-12 sm:w-16 sm:h-16 text-primary" />
                </div>
                <CardTitle className="text-lg sm:text-xl mb-2 sm:mb-3">פתרונות יצירתיים</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm sm:text-base">
                  אנו לא פועלים כרובוטים. אנו בוחנים כל תיק לגופו, חושבים מחוץ לקופסה ומנהלים משא ומתן יעיל להשגת הסדרים המיטיבים עם הלקוח.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 sm:py-16 md:py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <SectionTitle 
            title="שאלות נפוצות בנושא הוצאה לפועל"
            align="center"
            className="mb-8 sm:mb-12"
          />
          
          <Accordion type="single" collapsible className="w-full space-y-4">
            <AccordionItem value="item-1" className="border rounded-lg px-4 sm:px-6">
              <AccordionTrigger className="text-base sm:text-lg font-semibold hover:no-underline text-right">
                קיבלתי מכתב התראה מלשכת ההוצאה לפועל, מה לעשות?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pt-2">
                אסור להתעלם! יש לך מספר ימים מוגבל (כמצוין במכתב) להגיב – לשלם את החוב, להגיש התנגדות או לבקש צו תשלומים. התעלמות תוביל להטלת עיקולים והגבלות. פנה לייעוץ משפטי באופן מיידי.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="border rounded-lg px-4 sm:px-6">
              <AccordionTrigger className="text-base sm:text-lg font-semibold hover:no-underline text-right">
                הטילו לי עיקול על חשבון הבנק. האם אפשר לבטל אותו?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pt-2">
                כן. במקרים רבים ניתן להגיש בקשה מנומקת לביטול העיקול, במיוחד אם הוא הוטל שלא כדין או אם אתה מציע הסדר תשלומים חלופי. פעולה מהירה היא המפתח.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="border rounded-lg px-4 sm:px-6">
              <AccordionTrigger className="text-base sm:text-lg font-semibold hover:no-underline text-right">
                מה זה "איחוד תיקים"?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pt-2">
                איחוד תיקים הוא הליך המאפשר לחייב שיש נגדו מספר תיקי הוצאה לפועל לרכז את כולם תחת תיק אחד (תיק איחוד) ולשלם תשלום חודשי אחד קבוע שמחולק בין כל הנושים.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="border rounded-lg px-4 sm:px-6">
              <AccordionTrigger className="text-base sm:text-lg font-semibold hover:no-underline text-right">
                אני זוכה, כמה זמן לוקח לגבות חוב?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pt-2">
                הדבר תלוי במורכבות התיק ובמצבו הכלכלי של החייב. ככל שפועלים מהר יותר, מטילים עיקולים ומאתרים נכסים – כך הסיכוי לגבות את החוב גדל. אנו פועלים בכל הערוצים כדי להאיץ את התהליך.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-12 sm:py-16 md:py-20 px-4 bg-muted/30">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6">
              אל תתמודדו עם ההוצאה לפועל לבד.
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              ההתנהלות מול המערכת דורשת מומחיות ופעולה נחרצת. בין אם אתם צריכים לגבות חוב או להתגונן מפניו, אנו כאן כדי לנהל את המאבק עבורכם.
            </p>
            <p className="text-base sm:text-lg font-semibold mt-4 sm:mt-6">
              צרו קשר עכשיו לפגישת ייעוץ דחופה וללא התחייבות.
            </p>
          </div>
          
          <div className="max-w-2xl mx-auto">
            <ContactForm />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Enforcement;
